import { Campaign, Ruleset } from 'entities/campaign';
import {
  EmailChannel,
  getEmailChannel,
  getStorageChannel,
  ScheduledChannel,
} from 'entities/campaign/rulesets/channels';
import { RulesetSplitTest } from 'entities/rulesetSplitTest';
import { Site } from 'sections/client-management/entities';
import { Audit } from 'entities/entity';
import { EntityWithId } from '../../../services/genericAPIGatewayService/interfaces';

/** Audit shape from expanded/wrapped API responses (created/lastUpdated with time, userId). */
export interface WrappedAudit {
  created?: { time?: number; userId?: string };
  lastUpdated?: { time?: number; userId?: string };
}

export interface RestEntity<TEntity> {
  id: string;
  value: TEntity;
  audit: WrappedAudit;
}

function normalizeWrappedAudit(audit: WrappedAudit): Audit {
  if (!audit) return {};
  return {
    createdAt: audit.created?.time,
    createdBy: audit.created?.userId,
    lastUpdatedAt: audit.lastUpdated?.time,
    lastUpdatedBy: audit.lastUpdated?.userId,
  };
}

/** Maps wrapped response (id, value, audit) to entity with normalized audit. */
export function mapWrappedResponseToEntity<T>(
  response: RestEntity<T>,
  parentResourceIds?: Record<string, string>,
): T & { audit: Audit } {
  return Object.assign({}, response.value, {
    audit: normalizeWrappedAudit(response.audit),
    ...(parentResourceIds || {}),
  }) as T & { audit: Audit };
}

type RestEntityWithChild<TEntity, TChild> = RestEntity<TEntity> & TChild;

type RulesetSplitTests = RestEntityWithChild<RulesetSplitTest, {}>;
type Rulesets = RestEntityWithChild<Ruleset, { ruleset_split_tests: RulesetSplitTests[] }>;
type Campaigns = RestEntityWithChild<Campaign, { message_rulesets: Rulesets[] }>;
export type ExpandedSite = RestEntityWithChild<Site, { campaigns: Campaigns[] }>;

export const rulesetSplitTests = (
  expandedSite: ExpandedSite,
): EntityWithId<RulesetSplitTest & { campaignId: string; rulesetId: string }>[] => {
  const result: EntityWithId<RulesetSplitTest & { campaignId: string; rulesetId: string }>[] = [];

  expandedSite.campaigns.forEach(campaign =>
    campaign.message_rulesets.forEach(ruleset =>
      ruleset.ruleset_split_tests.forEach(splitTest => {
        result.push({
          id: splitTest.id,
          entity: {
            ...mapWrappedResponseToEntity(splitTest),
            campaignId: campaign.id,
            rulesetId: ruleset.id,
          },
        });
      }),
    ),
  );
  return result;
};

export const rulesets = (expandedSite: ExpandedSite): EntityWithId<Ruleset & { campaignId: string }>[] => {
  const result: EntityWithId<Ruleset & { campaignId: string }>[] = [];

  expandedSite.campaigns.forEach(campaign =>
    campaign.message_rulesets.forEach(ruleset => {
      ruleset.value = fromStorageFormat(ruleset.value);
      result.push({
        id: ruleset.id,
        entity: {
          ...mapWrappedResponseToEntity(ruleset, { campaignId: campaign.id }),
          campaignId: campaign.id,
        },
      });
    }),
  );
  return result;
};

export function fromStorageFormat(ruleset: Ruleset): Ruleset {
  for (let i = 0; i < ruleset.channels?.length; i++) {
    if (ruleset.channels[i]?.type === '.ScheduledChannel') {
      ruleset.channels[i] = getEmailChannel(ruleset.channels[i] as ScheduledChannel);
    }
  }
  return ruleset;
}

export function toStorageFormat(ruleset: Ruleset): Ruleset {
  for (let i = 0; i < ruleset.channels?.length; i++) {
    if (ruleset.channels[i]?.type === '.Email') {
      ruleset.channels[i] = getStorageChannel(ruleset.channels[i] as EmailChannel);
    }
  }
  return ruleset;
}
